// =====================================================================
// طبقة Supabase Auth الجديدة — المرحلة 12ج-1
// ---------------------------------------------------------------------
// هذا الملف معزول تماماً عن منطق تسجيل الدخول الحالي. لا يستدعيه أي
// موديول حتى الآن. الدوال القديمة في supabaseClient.js (loginUser,
// updatePassword, getCurrentUser ... إلخ) تبقى كما هي وتعمل بالضبط
// كالمعتاد على anon key + password_hash. سيتم ربط هذه الطبقة فعلياً
// في المرحلة 12ج-3.
//
// كل دالة تعيد كائناً بنمط Supabase الموحّد: { data?, error } — حيث
// error إما null/undefined عند النجاح، أو رسالة/كائن خطأ عند الفشل.
// =====================================================================

import { supabase } from "./supabaseClient";

// ---------------------------------------------------------------------
// authSignIn — تسجيل دخول عبر Supabase Auth (email + password).
// عند النجاح يحفظ Supabase الجلسة تلقائياً في localStorage.
// يرجع: { data: { user, session }, error }.
// ---------------------------------------------------------------------
export async function authSignIn(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { data: null, error };
    return {
      data: { user: data?.user || null, session: data?.session || null },
      error: null,
    };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// authSignOut — تسجيل خروج كامل من Supabase Auth + تنظيف أي مفاتيح
// محلية قد تكون مرتبطة بالجلسة القديمة (currentUser).
// يرجع: { data: { signedOut: true }, error }.
// ---------------------------------------------------------------------
export async function authSignOut() {
  try {
    const { error } = await supabase.auth.signOut();
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem("currentUser");
      }
    } catch (_e) {
      // تجاهل أخطاء التخزين المحلي — ليست حاسمة لنجاح الخروج
    }
    if (error) return { data: null, error };
    return { data: { signedOut: true }, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// authGetSession — قراءة الجلسة الحالية من Supabase Auth.
// يرجع: { data: { session }, error } — session = null إذا لا توجد جلسة.
// ---------------------------------------------------------------------
export async function authGetSession() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) return { data: null, error };
    return { data: { session: data?.session || null }, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// authGetCurrentUser — قراءة المستخدم الحالي من Supabase Auth
// (وليس من localStorage 'currentUser' القديم).
// يرجع: { data: { user }, error } — user = { id, email, user_metadata,
// app_metadata, ... } أو null إن لم توجد جلسة.
// ---------------------------------------------------------------------
export async function authGetCurrentUser() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) return { data: null, error };
    const u = data?.user || null;
    return {
      data: {
        user: u
          ? {
              id: u.id,
              email: u.email,
              user_metadata: u.user_metadata || {},
              app_metadata: u.app_metadata || {},
            }
          : null,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// authOnStateChange — يشترك في تغيرات حالة المصادقة
// (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED ...).
// callback: (event, session) => void
// يرجع كائن Supabase الأصلي: { data: { subscription } }
// — يستدعي subscription.unsubscribe() لإلغاء الاشتراك.
// ---------------------------------------------------------------------
export function authOnStateChange(callback) {
  try {
    return supabase.auth.onAuthStateChange((event, session) => {
      try {
        if (typeof callback === "function") callback(event, session);
      } catch (_e) {
        // لا تكسر الاشتراك بسبب خطأ في الـ callback
      }
    });
  } catch (error) {
    return { data: { subscription: { unsubscribe: () => {} } }, error };
  }
}

// ---------------------------------------------------------------------
// authUpdatePassword — تغيير كلمة مرور المستخدم الحالي عبر Auth API
// (بدون hash يدوي). يستلزم وجود جلسة نشطة.
// يرجع: { data: { user }, error }.
// ---------------------------------------------------------------------
export async function authUpdatePassword(newPassword) {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) return { data: null, error };
    return { data: { user: data?.user || null }, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// authResetPassword — يرسل بريداً لإعادة تعيين كلمة المرور.
// سيستخدم في تدفق "نسيت كلمة المرور" في المراحل اللاحقة.
// يرجع: { data, error }.
// ---------------------------------------------------------------------
export async function authResetPassword(email) {
  try {
    const redirectTo =
      typeof window !== "undefined" && window.location
        ? `${window.location.origin}/reset-password`
        : undefined;
    const { data, error } = await supabase.auth.resetPasswordForEmail(
      email,
      redirectTo ? { redirectTo } : undefined
    );
    if (error) return { data: null, error };
    return { data: data || { sent: true }, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// linkAuthToUserRecord — يربط جلسة Auth الحالية بصف users المخصص.
// يقرأ users.role/department/... بناءً على البريد الإلكتروني للمستخدم
// الحالي في Auth. يستخدم لاحقاً لبناء كائن "currentUser" الموحّد.
// يرجع: { data: { id, role, department, name, email,
//                  must_change_password, is_active }, error }.
// ---------------------------------------------------------------------
export async function linkAuthToUserRecord(authUser) {
  try {
    if (!authUser || !authUser.email) {
      return { data: null, error: "Auth user أو البريد الإلكتروني مفقود" };
    }
    // roles[] هو مصدر الأدوار المتعددة. لو العمود غير موجود بعد (قبل تنفيذ
    // SQL المرحلة 1) نرجع لقراءة role وحده بدون ما يفشل الدخول.
    const BASE = "id, role, department, name, must_change_password, is_active, auth_user_id";
    let { data, error } = await supabase
      .from("users")
      .select(`${BASE}, roles`)
      .eq("email", authUser.email)
      .limit(1)
      .maybeSingle();
    if (error) {
      const res = await supabase
        .from("users")
        .select(BASE)
        .eq("email", authUser.email)
        .limit(1)
        .maybeSingle();
      data = res.data;
      error = res.error;
    }
    if (error) return { data: null, error };
    if (!data) {
      return {
        data: null,
        error: "لا يوجد صف مطابق في جدول users لهذا البريد",
      };
    }
    // الأدوار: roles[] إن وُجدت، وإلا الدور المفرد كمصفوفة من عنصر واحد
    const roles = Array.isArray(data.roles) && data.roles.length
      ? data.roles
      : (data.role ? [data.role] : []);
    return {
      data: {
        id: data.id,
        // authId = auth.users.id — هو ما يُرجعه auth.uid() في سياسات RLS.
        // public.users.id لا يساويه دائماً، لذلك نحمل الاثنين معاً.
        authId: authUser.id || data.auth_user_id || data.id,
        auth_user_id: data.auth_user_id || authUser.id || null,
        role: data.role,
        roles,
        department: data.department,
        name: data.name,
        email: authUser.email,
        must_change_password: data.must_change_password,
        is_active: data.is_active,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error };
  }
}

// ---------------------------------------------------------------------
// updateUserRoles — يحدّث أدوار مستخدم (المرحلة 1: الصلاحيات المتعددة).
// يكتب roles[] + الدور الأساسي في public.users فقط. مزامنة الأدوار مع
// auth.users.raw_app_meta_data (ومنها إلى الـ JWT الذي تقرأه سياسات RLS)
// يتولاها تريجر users_sync_roles_to_jwt في القاعدة — راجع
// scripts/multi-role-permissions.sql. الأدوار الجديدة تصل للتوكن عند
// تحديث الجلسة أو الدخول التالي.
// ---------------------------------------------------------------------
export async function updateUserRoles(userId, roles) {
  try {
    const list = Array.isArray(roles) ? [...new Set(roles.filter(Boolean))] : [];
    if (!userId) return { success: false, error: "معرّف المستخدم مفقود" };
    if (list.length === 0) return { success: false, error: "لا يمكن ترك المستخدم بدون أي دور" };

    // الدور الأساسي يبقى في العمود القديم role للتوافق مع بقية النظام
    const { error } = await supabase
      .from("users")
      .update({ roles: list, role: list[0] })
      .eq("id", userId);
    if (error) {
      return { success: false, error: error.message || "تعذّر تحديث الأدوار" };
    }
    return {
      success: true,
      roles: list,
      warning: "تُطبَّق الأدوار الجديدة على صلاحيات قاعدة البيانات بعد تحديث الجلسة أو الدخول التالي.",
    };
  } catch (err) {
    return { success: false, error: err?.message || String(err) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// adminCreateUser — Edge Function wrapper for atomic user creation
// ═══════════════════════════════════════════════════════════════════
// Used by UsersPage to create a new employee. The Edge Function
// creates the auth.users row and public.users row atomically with
// rollback on failure. Only callable by exec/secretary/system roles.

export async function adminCreateUser({
  email,
  name,
  role,
  roles,
  department,
  phone,
  temporaryPassword,
}) {
  // يقبل roles[] (جديد) أو role مفرد (قديم). الدور الأساسي = أول عنصر.
  const roleList = Array.isArray(roles) && roles.length
    ? roles.filter(Boolean)
    : (role ? [role] : []);
  const primaryRole = roleList[0] || role || "employee";
  try {
    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();

    if (sessionError || !sessionData?.session) {
      return {
        success: false,
        error: "انتهت الجلسة. سجّل دخولاً مجدداً.",
      };
    }

    const token = sessionData.session.access_token;
    const supabaseUrl = supabase.supabaseUrl;
    const anonKey = supabase.supabaseKey;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/admin-create-user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({
          email,
          name,
          role: primaryRole,
          roles: roleList,
          department,
          phone,
          temporaryPassword: temporaryPassword || undefined,
        }),
      }
    );

    const result = await response.json();

    if (!response.ok || !result.success) {
      return {
        success: false,
        error: result.error || "تعذّر إنشاء الموظف. حاول مجدداً.",
      };
    }

    // الـ Edge Function قد تتجاهل roles[] — نضمن كتابتها بعد الإنشاء.
    if (roleList.length > 1 && result.user?.id) {
      try {
        await supabase.from("users").update({ roles: roleList }).eq("id", result.user.id);
      } catch (_e) { /* الحساب أُنشئ — لا تُفشل العملية بسبب الأدوار الإضافية */ }
    }

    return {
      success: true,
      user: result.user,
      temporaryPassword: result.temporaryPassword,
      usedDefaultPassword: result.usedDefaultPassword,
    };
  } catch (err) {
    return {
      success: false,
      error: `خطأ شبكي: ${err.message || err}`,
    };
  }
}
